import { makeAutoObservable, runInAction } from "mobx";
import Parse from "parse";
import type {
  CalcSnapshot,
  DrinkItem,
  DrinkRecipe,
  EventSegment,
  FestivalConfig,
  FoodItem,
  Ingredient,
  InfraItem,
  ScenarioTag,
  SnapshotData,
} from "./types";

function parseUserToRef(
  user: Parse.User | undefined | null,
): Parse.User | null {
  return user ?? null;
}

// ─── Mappers: Parse.Object → plain type ──────────────────────────────────────

function mapConfig(obj: Parse.Object): FestivalConfig {
  return {
    objectId: obj.id,
    tags: obj.get("tags") ?? [],
    eventSegments: obj.get("eventSegments") ?? [],
    zusagenPercent: obj.get("zusagenPercent") ?? 50,
    aufbau: obj.get("aufbau") ?? 0,
    abbau: obj.get("abbau") ?? 0,
    activeTagId: obj.get("activeTagId") ?? null,
    locations: obj.get("locations") ?? [],
    drinkTypeMinimums: obj.get("drinkTypeMinimums") ?? {},
  };
}

function mapInfraItem(obj: Parse.Object): InfraItem {
  return {
    objectId: obj.id,
    name: obj.get("name") ?? "",
    itemType: obj.get("itemType") ?? "",
    active: obj.get("active") ?? true,
    tagPrices: obj.get("tagPrices") ?? [],
    order: obj.get("order") ?? 0,
    createdBy: obj.get("createdBy") ?? null,
    isActual: obj.get("isActual") ?? false,
    actualCost: obj.get("actualCost") ?? null,
  };
}

function mapFoodItem(obj: Parse.Object): FoodItem {
  return {
    objectId: obj.id,
    name: obj.get("name") ?? "",
    active: obj.get("active") ?? true,
    tagPrices: obj.get("tagPrices") ?? [],
    persons: obj.get("persons") ?? 0,
    eventSegmentIds: obj.get("eventSegmentIds") ?? [],
    order: obj.get("order") ?? 0,
    createdBy: obj.get("createdBy") ?? null,
    isActual: obj.get("isActual") ?? false,
    actualCost: obj.get("actualCost") ?? null,
  };
}

function mapDrinkItem(obj: Parse.Object): DrinkItem {
  const recipePtr = obj.get("recipe");
  return {
    objectId: obj.id,
    name: obj.get("name") ?? "",
    drinkType: obj.get("drinkType") ?? "",
    active: obj.get("active") ?? true,
    volumeMl: obj.get("volumeMl") ?? 1,
    servingsPerPerson: obj.get("servingsPerPerson") ?? 1,
    tagPrices: obj.get("tagPrices") ?? [],
    eventSegmentIds: obj.get("eventSegmentIds") ?? [],
    recipe: recipePtr
      ? { objectId: recipePtr.id, name: recipePtr.get("name") ?? "" }
      : null,
    order: obj.get("order") ?? 0,
    createdBy: obj.get("createdBy") ?? null,
    isActual: obj.get("isActual") ?? false,
    actualCost: obj.get("actualCost") ?? null,
  };
}

function mapRecipe(obj: Parse.Object): DrinkRecipe {
  return {
    objectId: obj.id,
    name: obj.get("name") ?? "",
    description: obj.get("description") ?? "",
    createdBy: obj.get("createdBy") ?? null,
  };
}

function mapIngredient(obj: Parse.Object): Ingredient {
  return {
    objectId: obj.id,
    active: obj.get("active") ?? true,
    name: obj.get("name") ?? "",
    quantity: obj.get("quantity") ?? 1,
    unit: obj.get("unit") ?? "",
    packageSize: obj.get("packageSize") ?? 1,
    packagePrice: obj.get("packagePrice") ?? 0,
    order: obj.get("order") ?? 0,
    createdBy: obj.get("createdBy") ?? null,
  };
}

function mapSnapshot(obj: Parse.Object): CalcSnapshot {
  return {
    objectId: obj.id,
    title: obj.get("title") ?? "",
    snapshotData: obj.get("snapshotData") ?? {},
    createdAt: obj.createdAt,
    createdBy: obj.get("createdBy") ?? null,
  };
}

// ─── Store ────────────────────────────────────────────────────────────────────

export class FestivalCalcStore {
  config: FestivalConfig = {
    tags: [],
    eventSegments: [],
    zusagenPercent: 50,
    aufbau: 0,
    abbau: 0,
    activeTagId: null,
    locations: [],
    drinkTypeMinimums: {},
  };

  infraItems: InfraItem[] = [];
  foodItems: FoodItem[] = [];
  drinkItems: DrinkItem[] = [];
  recipes: DrinkRecipe[] = [];
  ingredientsByRecipe: Map<string, Ingredient[]> = new Map();

  snapshots: CalcSnapshot[] = [];

  loading = true;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get tags(): ScenarioTag[] {
    return this.config.tags;
  }

  get segments(): EventSegment[] {
    return this.config.eventSegments;
  }

  get activeTagId(): string | null {
    return this.config.activeTagId;
  }

  // ─── Load ──────────────────────────────────────────────────────────────────

  async loadAll() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      const [
        configResults,
        infraResults,
        foodResults,
        drinkResults,
        recipeResults,
        snapshotResults,
      ] = await Promise.all([
        new Parse.Query("SCH_FestivalConfig").first(),
        new Parse.Query("SCH_InfraItem").ascending("order").find(),
        new Parse.Query("SCH_FoodItem").ascending("order").find(),
        new Parse.Query("SCH_DrinkItem")
          .include("recipe")
          .ascending("order")
          .find(),
        new Parse.Query("SCH_DrinkRecipe").ascending("name").find(),
        new Parse.Query("SCH_CalcSnapshot").descending("createdAt").find(),
      ]);

      runInAction(() => {
        this.config = configResults ? mapConfig(configResults) : this.config;
        this.infraItems = infraResults.map(mapInfraItem);
        this.foodItems = foodResults.map(mapFoodItem);
        this.drinkItems = drinkResults.map(mapDrinkItem);
        this.recipes = recipeResults.map(mapRecipe);
        this.snapshots = snapshotResults.map(mapSnapshot);
        this.loading = false;
      });
    } catch (e: any) {
      runInAction(() => {
        this.error = e?.message ?? "Fehler beim Laden";
        this.loading = false;
      });
    }
  }

  async loadIngredientsForRecipe(recipeId: string) {
    const recipe = new Parse.Object("SCH_DrinkRecipe");
    recipe.id = recipeId;
    const results = await new Parse.Query("SCH_Ingredient")
      .equalTo("recipe", recipe)
      .ascending("order")
      .find();
    runInAction(() => {
      this.ingredientsByRecipe.set(recipeId, results.map(mapIngredient));
    });
  }

  // ─── Config mutations ──────────────────────────────────────────────────────

  async setActiveTag(tagId: string | null) {
    runInAction(() => {
      this.config.activeTagId = tagId;
    });
    await this.saveConfig();
  }

  async saveConfig() {
    const obj = this.config.objectId
      ? Parse.Object.fromJSON({
          className: "SCH_FestivalConfig",
          objectId: this.config.objectId,
        })
      : new Parse.Object("SCH_FestivalConfig");
    obj.set("tags", this.config.tags);
    obj.set("eventSegments", this.config.eventSegments);
    obj.set("zusagenPercent", this.config.zusagenPercent);
    obj.set("aufbau", this.config.aufbau);
    obj.set("abbau", this.config.abbau);
    obj.set("activeTagId", this.config.activeTagId);
    obj.set("drinkTypeMinimums", this.config.drinkTypeMinimums);
    const saved = await obj.save();
    runInAction(() => {
      this.config.objectId = saved.id;
    });
  }

  async updateDrinkTypeMinimum(drinkType: string, value: number) {
    runInAction(() => {
      this.config.drinkTypeMinimums = {
        ...this.config.drinkTypeMinimums,
        [drinkType.replaceAll("$", "").replaceAll(".", "")]: value,
      };
    });
    await this.saveConfig();
  }

  // ─── Infra mutations ───────────────────────────────────────────────────────

  async addInfraItem() {
    const currentUser = Parse.User.current();
    const obj = new Parse.Object("SCH_InfraItem");
    obj.set("name", "");
    obj.set("itemType", "");
    obj.set("active", true);
    obj.set("tagPrices", []);
    obj.set("order", this.infraItems.length);
    obj.set("isActual", false);
    obj.set("actualCost", null);
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    runInAction(() => {
      this.infraItems.push(mapInfraItem(saved));
    });
  }

  async updateInfraItem(objectId: string, patch: Partial<InfraItem>) {
    runInAction(() => {
      const idx = this.infraItems.findIndex((i) => i.objectId === objectId);
      if (idx !== -1)
        this.infraItems[idx] = { ...this.infraItems[idx], ...patch };
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_InfraItem", objectId });
    for (const [key, val] of Object.entries(patch)) {
      if (key !== "objectId" && key !== "createdBy") obj.set(key, val);
    }
    await obj.save();
  }

  async deleteInfraItem(objectId: string) {
    runInAction(() => {
      this.infraItems = this.infraItems.filter((i) => i.objectId !== objectId);
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_InfraItem", objectId });
    await obj.destroy();
  }

  // ─── Food mutations ────────────────────────────────────────────────────────

  async addFoodItem() {
    const currentUser = Parse.User.current();
    const obj = new Parse.Object("SCH_FoodItem");
    obj.set("name", "");
    obj.set("active", true);
    obj.set("tagPrices", []);
    obj.set("persons", 0);
    obj.set("eventSegmentIds", []);
    obj.set("order", this.foodItems.length);
    obj.set("isActual", false);
    obj.set("actualCost", null);
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    runInAction(() => {
      this.foodItems.push(mapFoodItem(saved));
    });
  }

  async updateFoodItem(objectId: string, patch: Partial<FoodItem>) {
    runInAction(() => {
      const idx = this.foodItems.findIndex((i) => i.objectId === objectId);
      if (idx !== -1)
        this.foodItems[idx] = { ...this.foodItems[idx], ...patch };
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_FoodItem", objectId });
    for (const [key, val] of Object.entries(patch)) {
      if (key !== "objectId" && key !== "createdBy") obj.set(key, val);
    }
    await obj.save();
  }

  async deleteFoodItem(objectId: string) {
    runInAction(() => {
      this.foodItems = this.foodItems.filter((i) => i.objectId !== objectId);
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_FoodItem", objectId });
    await obj.destroy();
  }

  // ─── Drink mutations ───────────────────────────────────────────────────────

  async addDrinkItem() {
    const currentUser = Parse.User.current();
    const obj = new Parse.Object("SCH_DrinkItem");
    obj.set("name", "");
    obj.set("drinkType", "");
    obj.set("active", true);
    obj.set("volumeMl", 1);
    obj.set("servingsPerPerson", 1);
    obj.set("tagPrices", []);
    obj.set("eventSegmentIds", []);
    obj.set("order", this.drinkItems.length);
    obj.set("isActual", false);
    obj.set("actualCost", null);
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    runInAction(() => {
      this.drinkItems.push(mapDrinkItem(saved));
    });
  }

  async updateDrinkItem(objectId: string, patch: Partial<DrinkItem>) {
    runInAction(() => {
      const idx = this.drinkItems.findIndex((i) => i.objectId === objectId);
      if (idx !== -1)
        this.drinkItems[idx] = { ...this.drinkItems[idx], ...patch };
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_DrinkItem", objectId });
    for (const [key, val] of Object.entries(patch)) {
      if (key !== "objectId" && key !== "createdBy" && key !== "recipe")
        obj.set(key, val);
      if (key === "recipe" && (val as any)?.objectId) {
        const recipePtr = Parse.Object.fromJSON({
          className: "SCH_DrinkRecipe",
          objectId: (val as any).objectId,
        });
        obj.set("recipe", recipePtr);
      }
    }
    await obj.save();
  }

  async deleteDrinkItem(objectId: string) {
    runInAction(() => {
      this.drinkItems = this.drinkItems.filter((i) => i.objectId !== objectId);
    });
    const obj = Parse.Object.fromJSON({ className: "SCH_DrinkItem", objectId });
    await obj.destroy();
  }

  // ─── Recipe mutations ──────────────────────────────────────────────────────

  async addRecipe(name: string): Promise<DrinkRecipe> {
    const currentUser = Parse.User.current();
    const obj = new Parse.Object("SCH_DrinkRecipe");
    obj.set("name", name);
    obj.set("description", "");
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    const recipe = mapRecipe(saved);
    runInAction(() => {
      this.recipes.push(recipe);
    });
    return recipe;
  }

  async updateRecipe(objectId: string, patch: Partial<DrinkRecipe>) {
    runInAction(() => {
      const r = this.recipes.find((r) => r.objectId === objectId);
      if (r) Object.assign(r, patch);
    });
    const obj = Parse.Object.fromJSON({
      className: "SCH_DrinkRecipe",
      objectId,
    });
    for (const [key, val] of Object.entries(patch)) {
      if (key !== "objectId" && key !== "createdBy") obj.set(key, val);
    }
    await obj.save();
  }

  // ─── Ingredient mutations ──────────────────────────────────────────────────

  async addIngredient(recipeId: string) {
    const currentUser = Parse.User.current();
    const recipePtr = Parse.Object.fromJSON({
      className: "SCH_DrinkRecipe",
      objectId: recipeId,
    });
    const existing = this.ingredientsByRecipe.get(recipeId) ?? [];
    const obj = new Parse.Object("SCH_Ingredient");
    obj.set("recipe", recipePtr);
    obj.set("active", true);
    obj.set("name", "");
    obj.set("quantity", 1);
    obj.set("unit", "");
    obj.set("packageSize", 1);
    obj.set("packagePrice", 0);
    obj.set("order", existing.length);
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    runInAction(() => {
      const updated = [
        ...(this.ingredientsByRecipe.get(recipeId) ?? []),
        mapIngredient(saved),
      ];
      this.ingredientsByRecipe.set(recipeId, updated);
    });
  }

  async updateIngredient(
    recipeId: string,
    objectId: string,
    patch: Partial<Ingredient>,
  ) {
    runInAction(() => {
      const list = this.ingredientsByRecipe.get(recipeId) ?? [];
      const item = list.find((i) => i.objectId === objectId);
      if (item) Object.assign(item, patch);
    });
    const obj = Parse.Object.fromJSON({
      className: "SCH_Ingredient",
      objectId,
    });
    for (const [key, val] of Object.entries(patch)) {
      if (key !== "objectId" && key !== "createdBy" && key !== "recipe")
        obj.set(key, val);
    }
    await obj.save();
  }

  async deleteIngredient(recipeId: string, objectId: string) {
    runInAction(() => {
      const updated = (this.ingredientsByRecipe.get(recipeId) ?? []).filter(
        (i) => i.objectId !== objectId,
      );
      this.ingredientsByRecipe.set(recipeId, updated);
    });
    const obj = Parse.Object.fromJSON({
      className: "SCH_Ingredient",
      objectId,
    });
    await obj.destroy();
  }

  // ─── Snapshots ─────────────────────────────────────────────────────────────

  async saveSnapshot(title: string) {
    const currentUser = Parse.User.current();
    const snapshotData: SnapshotData = {
      config: { ...this.config },
      infraItems: [...this.infraItems],
      foodItems: [...this.foodItems],
      drinkItems: [...this.drinkItems],
    };
    const obj = new Parse.Object("SCH_CalcSnapshot");
    obj.set("title", title);
    obj.set("snapshotData", snapshotData);
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    runInAction(() => {
      this.snapshots.unshift(mapSnapshot(saved));
    });
  }

  async deleteSnapshot(objectId: string) {
    runInAction(() => {
      this.snapshots = this.snapshots.filter((s) => s.objectId !== objectId);
    });
    const obj = Parse.Object.fromJSON({
      className: "SCH_CalcSnapshot",
      objectId,
    });
    await obj.destroy();
  }
}

export const festivalCalcStore = new FestivalCalcStore();
